<script setup lang="ts">
/**
 *
 * @name ViewDetailsModal
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE QUE MUESTRA LOS DETALLES DE UN PAQUETE DE CERTIFICADOS
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA INFORMACIÓN DETALLADA DE UN PAQUETE DE CERTIFICADOS
 * R2: PERMITE VER LA LISTA DE CERTIFICADOS INCLUIDOS EN EL PAQUETE
 * R3: PERMITE VER LA INFORMACIÓN DE LOS RESPONSABLES DEL PAQUETE
 * R4: PERMITE VER EL ESTADO DE FIRMA DEL PAQUETE
 * R5: PERMITE ABRIR LOS DETALLES DE UN CERTIFICADO ESPECÍFICO
 * R6: PERMITE MANEJAR EL MODO DE PRUEBA
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { computed, ref, watch } from 'vue'
import { ICertificatePackage, ICertificate } from '@shared/types'
import CertificateDetailsModal from './CertificateDetailsModal.vue'

/**
 * @description Props del componente
 * @property {boolean} modelValue - Controla la visibilidad del modal
 * @property {ICertificatePackage | null} package - Paquete de certificados a mostrar
 * @property {boolean} isTestMode - Indica si el componente está en modo prueba
 * @property {ICertificate[]} certificatesList - Lista de certificados incluidos en el paquete
 */
const props = defineProps<{
	modelValue: boolean
	package: ICertificatePackage | null
	isTestMode: boolean
	certificatesList: ICertificate[]
	currentInstitution: string
}>()

/**
 * @description Emits del componente
 * @param {string} update:modelValue - Emitido cuando cambia la visibilidad del modal
 */
const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()

/**
 * @description Computed property que controla la visibilidad del modal
 * @type {ComputedRef<boolean>}
 */
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

/**
 * @description Referencia que almacena el paquete local
 * @type {Ref<ICertificatePackage | null>}
 */
const localPackage = ref<ICertificatePackage | null>(null)

/**
 * @description Referencia que controla la visibilidad del modal de detalles del certificado
 * @type {Ref<boolean>}
 */
const showCertificate = ref(false)

/**
 * @description Referencia que almacena el certificado seleccionado
 * @type {Ref<ICertificate | null>}
 */
const localCertificate = ref<ICertificate | null>(null)

/**
 * @description Computed property que determina el estado del paquete
 * @type {ComputedRef<string>}
 * @returns {string} 'Firmado' si el paquete está firmado, 'Pendiente' en caso contrario
 */
const estado = computed(() => {
	if (localPackage.value?.sign) {
		return 'Firmado'
	} else {
		return 'Pendiente'
	}
})

/**
 * @description Computed property que obtiene la lista de responsables del paquete
 * @type {ComputedRef<Array<{name: string, sign: boolean, cargo: string, curp: string}>>}
 */
const responsables = computed(() =>
	Object.keys(localPackage.value?.responsables || {}).map(key => ({
		name: localPackage.value?.responsables[key].name,
		sign: localPackage.value?.responsables[key].sign,
		cargo: localPackage.value?.responsables[key].cargo,
		curp: key
	}))
)

/**
 * @description Watcher que actualiza el paquete local cuando cambia el prop
 */
watch(
	() => props.package,
	newPackage => {
		localPackage.value = newPackage ? { ...newPackage } : null
	},
	{ immediate: true }
)

/**
 * @description Función que formatea una fecha
 * @param {string | number | Date} date - Fecha a formatear
 * @returns {string} Fecha formateada o 'No disponible' si no hay fecha
 */
const formatDate = (date: string | number | Date) => {
	if (!date) return 'No disponible'
	return new Date(date).toLocaleString('es-MX', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})
}

/**
 * @description Función que abre el modal de detalles de un certificado
 * @param {ICertificate} certificate - Certificado a mostrar
 */
const openCertificateDetails = (certificate: ICertificate) => {
	showCertificate.value = true
	localCertificate.value = certificate
}

/**
 * @description Función para cerrar el modal
 */
const closeModalDetails = () => {
	showModal.value = false
}
</script>
<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard v-if="localPackage" class="max-w-4xl mx-auto">
			<!-- Header mejorado -->
			<VaCardTitle :class="[props.isTestMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left">
						<VaIcon name="description" class="text-white" />
						<h2 class="text-xl font-bold text-white">Detalles del Paquete</h2>
						<p class="text-sm text-blue-100 mt-1 text-white">Información completa del paquete de certificados.</p>
					</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.isTestMode ? '#0d6efd' : '#7f1d1d'" type="button" @click="closeModalDetails" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="p-6">
				<!-- Información del Paquete -->
				<section class="package-section">
					<h3 class="section-title">
						<VaIcon name="description" class="title-icon" />
						Información del Paquete
					</h3>
					<div class="package-grid">
						<!-- Nombre del Paquete -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="badge" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Nombre del Paquete</h5>
									<p class="package-value">{{ localPackage.label }}</p>
								</div>
							</div>
						</div>

						<!-- Fecha -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="event" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Fecha</h5>
									<p class="package-value">{{ formatDate(localPackage.id) }}</p>
								</div>
							</div>
						</div>

						<!-- Número de Certificados -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="collections_bookmark" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Certificados</h5>
									<p class="package-value">{{ localPackage.numCertificates }}</p>
								</div>
							</div>
						</div>

						<!-- Estado de Firma -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="check_circle" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Estado</h5>
									<p :class="['package-value', estado === 'Firmado' ? 'text-success' : 'text-warning']">
										{{ estado }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Responsables de Firma -->
				<section class="signers-section">
					<h4 class="signers-title">
						<VaIcon name="groups" class="title-icon" />
						Responsables de Firma
					</h4>
					<div class="signers-grid">
						<div v-for="(responsable, index) in responsables" :key="index" class="signer-card">
							<div class="signer-header">
								<div class="signer-avatar">
									<VaIcon name="person" class="signer-icon" />
								</div>
								<div class="signer-info">
									<h5 class="signer-name">{{ responsable.name }}</h5>
									<p class="signer-role">{{ responsable.cargo }}</p>
									<p class="signer-curp">CURP: {{ responsable.curp }}</p>
								</div>
								<div class="signer-status">
									<span :class="['status-badge', responsable.sign ? 'status-signed' : 'status-pending']">
										{{ responsable.sign ? 'Firmado' : 'Pendiente' }}
									</span>
									<VaIcon v-if="responsable.sign" name="check_circle" class="sign-icon" />
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Certificados del Paquete -->
				<section class="certificates-section">
					<h4 class="section-title">
						<VaIcon name="school" class="title-icon" />
						Certificados del Paquete
					</h4>

					<div v-if="certificatesList.length === 0" class="empty-state">
						<div class="empty-icon">
							<VaIcon name="info" class="w-12 h-12 text-gray-400" />
						</div>
						<p class="text-gray-500 text-lg">No se encontraron certificados en este paquete.</p>
					</div>

					<div v-else class="certificates-grid">
						<div v-for="(certificate, index) in certificatesList" :key="index" class="certificate-card">
							<div class="certificate-header">
								<div class="certificate-avatar">
									<VaIcon name="person" class="certificate-icon" />
								</div>
								<div class="certificate-info">
									<h5 class="certificate-name">
										{{ certificate.data.Alumno.nombre.toUpperCase() }}
										{{ certificate.data.Alumno.primerApellido.toUpperCase() }}
										{{ certificate.data.Alumno.segundoApellido.toUpperCase() }}
									</h5>
									<p class="certificate-control">Control: {{ certificate.data.numeroControl }}</p>
								</div>
							</div>

							<div class="certificate-actions">
								<button class="btn btn-primary btn-sm" @click="openCertificateDetails(certificate)">
									<VaIcon name="visibility" class="btn-icon" />
									Ver Detalles
								</button>
							</div>
						</div>
					</div>
				</section>
			</VaCardContent>
		</VaCard>
	</VaModal>
	<CertificateDetailsModal v-model="showCertificate" :certificate="localCertificate!" :isTestMode="props.isTestMode" :currentInstitution="props.currentInstitution" />
</template>

<style scoped>
/* Estados de texto */
.text-success {
	color: #28a745;
}
.text-warning {
	color: #ffc107;
}

/* Secciones principales */
.package-section,
.signers-section,
.certificates-section {
	margin-top: 1rem;
	margin-bottom: 1rem;
}

.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Grid de paquetes */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 1rem;
}

.package-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.package-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.package-header {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.package-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.package-icon {
	color: white;
	font-size: 1.25rem;
}

.package-info {
	flex: 1;
	min-width: 0;
}

.package-name {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6c757d;
	margin: 0 0 0.25rem 0;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.package-value {
	font-size: 1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0;
	line-height: 1.3;
}

/* Sección de firmantes */
.signers-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.signers-grid {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.signer-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.signer-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.signer-header {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.signer-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.signer-icon {
	color: white;
	font-size: 1.25rem;
}

.signer-info {
	flex: 1;
	min-width: 0;
}

.signer-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.signer-role {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0 0 0.25rem 0;
	font-weight: 500;
}

.signer-curp {
	font-size: 0.8rem;
	color: #6c757d;
	margin: 0;
	font-weight: 400;
}

.signer-status {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
}

.status-badge {
	padding: 0.25rem 0.75rem;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.status-signed {
	background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
	color: #166534;
	border: 1px solid #86efac;
}

.status-pending {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}

.sign-icon {
	color: #28a745;
	font-size: 1.25rem;
	animation: fadeIn 0.5s ease-in-out;
}

/* Sección de certificados */
.certificates-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1rem;
	max-height: 500px;
	overflow-y: auto;
	padding-right: 0.5rem;
}

.certificate-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.certificate-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.certificate-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
}

.certificate-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.certificate-icon {
	color: white;
	font-size: 1.25rem;
}

.certificate-info {
	flex: 1;
	min-width: 0;
}

.certificate-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.certificate-control {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
	font-weight: 500;
}

.certificate-actions {
	display: flex;
	justify-content: flex-end;
}

/* Estados de carga y vacío */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem 1rem;
	text-align: center;
}

.empty-icon {
	margin-bottom: 1rem;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
	transform: none;
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

.btn-sm {
	padding: 0.375rem 0.75rem;
	font-size: 0.75rem;
}

.btn-icon {
	width: 1rem;
	height: 1rem;
	margin-right: 0.5rem;
}

/* Scroll personalizado */
.certificates-grid::-webkit-scrollbar {
	width: 8px;
}

.certificates-grid::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 4px;
}

.certificates-grid::-webkit-scrollbar-thumb {
	background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
	border-radius: 4px;
}

.certificates-grid::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.package-card,
.signer-card,
.certificate-card {
	animation: fadeIn 0.5s ease-in-out;
}

/* Responsive design */
@media (max-width: 768px) {
	.package-grid,
	.certificates-grid {
		grid-template-columns: 1fr;
	}

	.signer-header,
	.certificate-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.75rem;
	}

	.signer-status {
		align-self: flex-end;
	}

	.signer-card,
	.package-card,
	.certificate-card {
		padding: 1rem;
	}
}

/* Gradientes para el header */
.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
</style>
